import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Bell, Images, Layers, Mail, X } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const APP_STATUS = {
  live: { label: "apps_live", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  coming_soon: { label: "apps_coming", cls: "text-amber-400 border-amber-400/40 bg-amber-400/10" },
  developing: { label: "apps_developing", cls: "text-sky-400 border-sky-400/40 bg-sky-400/10" },
  ready: { label: "apps_ready", cls: "text-violet-400 border-violet-400/40 bg-violet-400/10" },
};

function NotifyForm({ app, testId }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error(t("kit_email_invalid"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/apps/${app.id}/interest`, { email: email.trim() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p data-testid={`${testId}-done`} className="mt-5 text-xs text-emerald-400 flex items-center gap-1.5">
        <Bell size={13} /> {t("apps_notify_done")}
      </p>
    );
  }
  return (
    <div className="mt-5 flex gap-2">
      <input
        data-testid={`${testId}-email`}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("apps_notify_ph")}
        className="flex-1 min-w-0 bg-[#0B1120] border border-white/10 text-slate-100 placeholder:text-slate-600 text-xs px-3 py-2 outline-none focus:border-[#ED1C24] transition-colors"
      />
      <button
        data-testid={`${testId}-btn`}
        onClick={submit}
        disabled={busy}
        className="inline-flex items-center gap-1.5 bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-50 text-white text-xs font-medium px-3.5 transition-colors duration-300"
      >
        <Bell size={12} /> {busy ? "…" : t("apps_notify")}
      </button>
    </div>
  );
}

export default function Apps() {
  const { t } = useLang();
  const [apps, setApps] = useState(null);
  const [galleryFor, setGalleryFor] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  const extUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);

  useEffect(() => {
    api.get("/apps").then((r) => setApps(r.data)).catch(() => setApps([]));
  }, []);

  const openGallery = (a) => {
    setGalleryFor(a);
    setActiveImg(0);
  };

  const galleryImages = galleryFor
    ? [{ url: galleryFor.image, caption: "" }, ...(galleryFor.gallery || [])]
        .map((g) => (typeof g === "string" ? { url: g, caption: "" } : g))
        .filter((g) => g.url)
    : [];

  return (
    <main data-testid="apps-page" className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
      <Reveal y={24}>
        <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">{t("apps_label")}</p>
        <h1 className="font-display font-semibold text-white text-4xl sm:text-6xl tracking-tight mb-4">
          {t("apps_title")}<span className="text-[#FFC61A]">.</span>
        </h1>
        <p className="text-slate-400 max-w-xl mb-14">{t("apps_sub")}</p>
      </Reveal>

      {apps && apps.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {apps.map((a, i) => {
            const shots = (a.gallery || []).length;
            const firstShot = (a.gallery || [])[0];
            const cover = a.image || (typeof firstShot === "string" ? firstShot : firstShot?.url) || "";
            return (
              <Reveal key={a.id} delay={(i % 3) * 0.08}>
                <article
                  data-testid={`app-card-${i}`}
                  className="group h-full bg-[#0F172A] border border-white/10 hover:border-[#ED1C24]/60 hover:-translate-y-2 transition-[border-color,transform] duration-500 flex flex-col overflow-hidden"
                >
                  {cover && (
                    <div
                      className={`relative overflow-hidden bg-black/50 ${shots > 0 ? "cursor-pointer" : ""}`}
                      onClick={() => shots > 0 && openGallery(a)}
                    >
                      <img
                        src={imgUrl(cover)}
                        alt={a.name}
                        loading="lazy"
                        className="w-full aspect-[4/3] object-cover object-top block group-hover:scale-105 transition-transform duration-700"
                      />
                      {shots > 0 && (
                        <button
                          data-testid={`app-gallery-btn-${i}`}
                          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-[#0B1120]/85 backdrop-blur border border-white/20 hover:border-[#ED1C24] text-white text-[11px] px-3 py-1.5 transition-colors duration-300"
                        >
                          <Images size={12} /> {a.image ? shots + 1 : shots} {t("photos_label")}
                        </button>
                      )}
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {a.tag && (
                        <span className="text-[10px] tracking-[0.15em] uppercase bg-[#ED1C24]/10 text-[#ED1C24] border border-[#ED1C24]/30 px-2.5 py-1">
                          {a.tag}
                        </span>
                      )}
                      <span
                        data-testid={`app-status-${i}`}
                        className={`text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 border ${(APP_STATUS[a.status] || APP_STATUS.coming_soon).cls}`}
                      >
                        {t((APP_STATUS[a.status] || APP_STATUS.coming_soon).label)}
                      </span>
                    </div>
                    <h2 className="font-display font-semibold text-white text-xl leading-snug">{a.name}</h2>
                    {a.tagline && <p className="text-[#FFC61A] text-sm mt-1">{a.tagline}</p>}
                    {a.description && (
                      <p className="text-sm text-slate-400 leading-relaxed mt-3 flex-1">{a.description}</p>
                    )}
                    {a.status === "developing" && (
                      <div data-testid={`app-progress-${i}`} className="mt-4">
                        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.15em] text-sky-400 mb-1.5">
                          <span>{t("apps_progress")}</span>
                          <span data-testid={`app-progress-value-${i}`}>{Math.min(100, Math.max(0, a.progress || 0))}%</span>
                        </div>
                        <div className="h-1.5 bg-white/10 overflow-hidden">
                          <div
                            className="h-full bg-sky-400 transition-[width] duration-700"
                            style={{ width: `${Math.min(100, Math.max(0, a.progress || 0))}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {a.url ? (
                      <a
                        data-testid={`app-visit-${i}`}
                        href={extUrl(a.url)}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-5 inline-flex items-center gap-2 text-sm text-white hover:text-[#FFC61A] transition-colors duration-300 border-b border-white/20 hover:border-[#FFC61A] pb-1 self-start"
                      >
                        {t("apps_visit")} <ArrowUpRight size={15} />
                      </a>
                    ) : a.status === "live" ? (
                      <span className="mt-5 text-xs text-slate-600">{t("apps_soon_link")}</span>
                    ) : null}
                    {a.status !== "live" && <NotifyForm app={a} testId={`app-notify-${i}`} />}
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      ) : apps ? (
        <Reveal>
          <div data-testid="apps-empty" className="max-w-xl border border-white/10 bg-[#0F172A]/60 p-10 text-center">
            <Layers size={36} className="text-[#ED1C24] mx-auto mb-5" />
            <p className="font-display font-semibold text-white text-2xl mb-3">{t("apps_empty_title")}</p>
            <p className="text-slate-400 text-sm mb-8">{t("apps_empty_sub")}</p>
            <Link
              data-testid="apps-contact-btn"
              to="/support"
              className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-8 py-3.5 transition-colors duration-300"
            >
              <Mail size={15} /> {t("apps_empty_cta")}
            </Link>
          </div>
        </Reveal>
      ) : null}

      <Dialog open={!!galleryFor} onOpenChange={(o) => !o && setGalleryFor(null)}>
        <DialogContent data-testid="app-gallery-dialog" className="bg-[#0F172A] border-white/10 text-slate-100 rounded-none max-w-3xl p-0 overflow-hidden">
          <VisuallyHidden><DialogTitle>{galleryFor?.name}</DialogTitle></VisuallyHidden>
          {galleryFor && galleryImages.length > 0 && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-4 px-1">
                <p className="font-display font-semibold text-white text-lg">{galleryFor.name}</p>
                <button
                  data-testid="gallery-close-btn"
                  onClick={() => setGalleryFor(null)}
                  className="w-9 h-9 flex items-center justify-center border border-white/15 text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors duration-300"
                  aria-label="Close gallery"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="border border-white/10 overflow-hidden mb-2">
                <img
                  data-testid="gallery-main-image"
                  src={imgUrl(galleryImages[activeImg].url)}
                  alt={galleryFor.name}
                  className="w-full h-[420px] object-contain bg-black/40"
                />
              </div>
              {galleryImages[activeImg].caption && (
                <p data-testid="gallery-caption" className="text-sm text-slate-400 mb-3 leading-relaxed">
                  {galleryImages[activeImg].caption}
                </p>
              )}
              <div className="flex gap-3 overflow-x-auto pb-1 mt-2">
                {galleryImages.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`shrink-0 w-20 h-16 overflow-hidden border transition-colors duration-300 ${
                      i === activeImg ? "border-[#ED1C24]" : "border-white/10 hover:border-white/40"
                    }`}
                  >
                    <img src={imgUrl(g.url)} alt={g.caption || ""} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
